import { MAX_GLOBAL_SEARCH_OPTIONS } from "@/constants";
import { ContractStatusType } from "@/gql/graphql";
import { Icon, MenuItem } from "@trimble/modus-cmx";
import Badge from "@/components/Badge";
import { useTranslation } from "react-i18next";
import { highlightMatch } from "@/components/app/Navigation/GlobalHeader/helper";
import type { ContractOptionProps } from "@/components/Navigation/GlobalHeader/GlobalSearch/types";
import { ViewAllOption } from "../ViewAllOption/ViewAllOption";

type BadgeColor =
  | "success"
  | "warning"
  | "danger"
  | "primary"
  | "secondary"
  | "tertiary"
  | "high-contrast";

const statusBadgeColor: Partial<Record<ContractStatusType, BadgeColor>> = {
  [ContractStatusType.Activated]: "success",
  [ContractStatusType.AmendmentActivated]: "success",
  [ContractStatusType.AmendmentDraft]: "high-contrast",
  [ContractStatusType.AmendmentSubmitted]: "high-contrast",
  [ContractStatusType.AmendmentActivationInProgress]: "high-contrast",
  [ContractStatusType.AmendmentActivationAwaitingStartDate]: "high-contrast",
};

export function ContractOption({
  options,
  totalCount,
  searchTerm,
  onViewAll,
}: ContractOptionProps) {
  const { t } = useTranslation();

  const optionsToDisplay = options.slice(0, MAX_GLOBAL_SEARCH_OPTIONS);

  return (
    <>
      {optionsToDisplay.map((option) => (
        <MenuItem key={option.contractId} value={option.contractId}>
          <div slot="start-icon" className="option">
            <Icon
              name="document"
              variant="solid"
              size="xs"
              decorative
              className="icon-color"
            />
            <div className="option-details">
              <span className="option-contract-number">
                {highlightMatch(option.contractNumber, searchTerm)}
              </span>
              <span
                className="option-customer-name"
                style={{ textTransform: "capitalize" }}
              >
                {highlightMatch(option.customerName?.toLowerCase(), searchTerm)}
              </span>
              {option.purchaseOrderNumber && (
                <span className="option-po-number">
                  {" "}
                  {t("global_search.contract_PO_number")}{" "}
                  {highlightMatch(option.purchaseOrderNumber, searchTerm)}
                </span>
              )}
            </div>
            {option.contractStatus && (
              <Badge
                size="md"
                variant="outlined"
                color={statusBadgeColor[option.contractStatus]}
                className="option-badge"
              >
                {t(`contracts.statuses.${option.contractStatus.toLowerCase()}`)}
              </Badge>
            )}
          </div>
        </MenuItem>
      ))}
      {totalCount > MAX_GLOBAL_SEARCH_OPTIONS && (
        <ViewAllOption
          totalCount={totalCount}
          onViewAll={onViewAll}
          searchCategory="contracts"
        />
      )}
    </>
  );
}
